/**
 * Entity — мастер-таблица «компания» (юрлицо). Replaces подвешенный entity_id.
 * Multi-tenancy: каждая сущность с entity_id ссылается на entities.id.
 * S-multi-1 BM-1-2.
 */
import { z } from 'zod';
import { IsoDateTime, UuidString } from '../primitives.js';
import { Holding } from './holding.js';

export const EntityStatus = z.enum(['active', 'archived']);
export type EntityStatus = z.infer<typeof EntityStatus>;

/**
 * Currency для tenant_config / Entity.default_currency.
 * Initial scope — KZ + основные ISO-4217 для multi-tenancy MVP. Расширяемо.
 */
export const EntityCurrency = z.enum(['KZT', 'USD', 'EUR', 'RUB']);
export type EntityCurrency = z.infer<typeof EntityCurrency>;

/** БИН (KZ Business Identification Number) — 12 цифр. */
const BinRegex = /^\d{12}$/;

export const Entity = z.object({
  id: UuidString,
  holding_id: UuidString.nullable(),
  name: z.string().min(1).max(200),
  bin: z.string().regex(BinRegex, 'БИН должен быть 12 цифр').nullable(),
  address: z.string().max(500).nullable(),
  default_currency: EntityCurrency.default('KZT'),
  status: EntityStatus,
  created_at: IsoDateTime,
  updated_at: IsoDateTime,
});
export type Entity = z.infer<typeof Entity>;

export const EntityCreate = z
  .object({
    name: z.string().min(1).max(200),
    holding_id: UuidString.nullable().optional(),
    bin: z.string().regex(BinRegex).nullable().optional(),
    address: z.string().max(500).nullable().optional(),
    default_currency: EntityCurrency.optional().default('KZT'),
    status: EntityStatus.optional().default('active'),
  })
  // zod-strict-ok: input request-body
  .strict();
export type EntityCreate = z.infer<typeof EntityCreate>;

export const EntityPatch = z
  .object({
    name: z.string().min(1).max(200).optional(),
    holding_id: UuidString.nullable().optional(),
    bin: z.string().regex(BinRegex).nullable().optional(),
    address: z.string().max(500).nullable().optional(),
    default_currency: EntityCurrency.optional(),
    status: EntityStatus.optional(),
  })
  // zod-strict-ok: input request-body
  .strict();
export type EntityPatch = z.infer<typeof EntityPatch>;

/** Bundle-response: Entity + holding (denorm). */
export const EntityWithHolding = Entity.extend({
  holding: Holding.nullable(),
});
export type EntityWithHolding = z.infer<typeof EntityWithHolding>;

export const EntityList = z.object({
  items: z.array(EntityWithHolding),
  next_cursor: z.string().nullable().optional(),
});
export type EntityList = z.infer<typeof EntityList>;
